import { useState } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useStore } from "../../../contexts/StoreContext";
import { rewardSummaryPreview } from "../../../utils/passUtils";
import code128Image from "../../../assets/code128.png";
import aztecImage from "../../../assets/aztec.png";
import pdf417Image from "../../../assets/pdf417.png";
import qrImage from "../../../assets/qr.png";

// Build the back-of-card content, mirroring build_back_fields in the wallet
// services. The two platforms render the same data very differently:
//   - Apple  (apple_wallet)  → a flat list of label/value backFields.
//   - Google (google_wallet) → custom fields become stacked "text modules" and
//     every link (referral, review, custom links, Powered By) is collapsed into
//     a single "Links" module.
// We return both pieces so the preview can render either platform accurately.
// The "Powered By" link is appended last and only on starter/free plans.
function getBackContent(formData, isStarterPlan) {
  const customFields = [];
  if (formData.enableCustomBackFields && formData.customBackFields) {
    formData.customBackFields.forEach((field) => {
      if (field?.label || field?.value) {
        customFields.push({ label: field.label || "", value: field.value || "" });
      }
    });
  }

  // `label`/`value` drive the Apple row; `android` is the Google link description.
  const links = [];
  if (formData.enableReferalProgram) {
    links.push({
      label: "Referral Program",
      value: "Click to share",
      android: "Share Referral Program",
    });
  }
  if (formData.enableReviewFeedbackLink) {
    const label = formData.reviewFeedback?.label || "Review & Feedback";
    links.push({
      label,
      value: "Click to review",
      android: formData.reviewFeedback?.label || "Click to review",
    });
  }
  if (formData.enableLinks && formData.links) {
    formData.links.forEach((linkItem) => {
      if (linkItem?.label || linkItem?.url) {
        links.push({
          label: linkItem.label || "",
          value: "Visit",
          android: linkItem.label || "Visit",
        });
      }
    });
  }

  const hasMerchantContent = customFields.length > 0 || links.length > 0;

  if (isStarterPlan) {
    links.push({
      label: "Powered By",
      value: "Loyaltify.io",
      android: "Powered By Loyaltify.io",
    });
  }

  return { customFields, links, hasMerchantContent };
}

// Background/hero image with the stamp grid overlaid. Shared by the Apple front
// (where it's the central strip and the capture source via `innerRef`) and the
// Google front (where the same image is the hero banner).
function StampStrip({ formData, getStampIconSource, innerRef }) {
  return (
    <div ref={innerRef} className="relative w-full">
      {formData.backgroundImage ? (
        <img
          src={formData.backgroundImage}
          alt="Background"
          className="w-full object-cover"
        />
      ) : (
        <div
          className="w-full"
          style={{
            aspectRatio: "1140/435",
            backgroundColor: formData.textColor || "#762105",
          }}
        />
      )}
      {formData.type === "stamp" && (
        <div
          className="absolute inset-0 px-8 py-1"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${
              formData.stampCount <= 4
                ? formData.stampCount
                : formData.stampCount <= 5
                  ? 3
                  : formData.stampCount <= 6
                    ? 3
                    : formData.stampCount <= 8
                      ? 4
                      : formData.stampCount <= 9
                        ? 3
                        : formData.stampCount <= 10
                          ? 5
                          : formData.stampCount <= 12
                            ? 4
                            : formData.stampCount <= 15
                              ? 5
                              : 6
            }, 1fr)`,
            gap: "0.75rem",
            alignItems: "center",
            justifyItems: "center",
          }}
        >
          {[...Array(parseInt(0 + formData.stampCount))].map((_, index) => {
            const baseSize =
              formData.stampCount <= 4
                ? 48
                : formData.stampCount <= 6
                  ? 44
                  : formData.stampCount <= 8
                    ? 40
                    : formData.stampCount <= 9
                      ? 36
                      : formData.stampCount <= 10
                        ? 40
                        : formData.stampCount <= 12
                          ? 36
                          : formData.stampCount <= 15
                            ? 32
                            : 28;

            const iconSize = baseSize * 0.75;

            // On milestone cards a slot can carry the reward's own icon (the
            // stamp position is index + 1); everything else uses the card's
            // default stamp icon.
            const milestoneIcon =
              formData.stampRewardMode === "milestone"
                ? (formData.milestones || []).find(
                    (m) => Number(m.position) === index + 1,
                  )?.icon
                : null;

            return (
              <div
                key={index}
                className="rounded-full flex items-center justify-center stamp-container"
                style={{
                  width: `${baseSize}px`,
                  height: `${baseSize}px`,
                  backgroundColor: formData.stampIconColor + "AA",
                  border: "2px solid " + formData.stampIconColor + "EE",
                }}
              >
                <img
                  src={milestoneIcon || getStampIconSource()}
                  alt="Stamp"
                  className="stamp-icon object-contain rounded-full"
                  style={{
                    width: `${iconSize}px`,
                    height: `${iconSize}px`,
                    opacity:
                      index <= formData.initialPoints - 1 ||
                      (formData.initialPoints == 0 && index == 0)
                        ? 1
                        : 0,
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Just the barcode image for a given type (null for "none"), so each platform
// can wrap it in its own container/caption.
function BarcodeImage({ barcodeType }) {
  switch (barcodeType) {
    case "qr":
      return <img src={qrImage} alt="QR Code" className="w-32 h-32" />;
    case "code128":
      return (
        <img src={code128Image} alt="Code 128 barcode" className="w-48 h-16" />
      );
    case "aztec":
      return <img src={aztecImage} alt="Aztec barcode" className="w-32 h-32" />;
    case "pdf417":
      return (
        <img
          src={pdf417Image}
          alt="PDF417 barcode"
          className="w-48 h-16 object-contain"
        />
      );
    default:
      return null;
  }
}

// Apple-style barcode block: barcode + id caption inside a white rounded box.
function BarcodeBlock({ formData }) {
  return (
    <div className="flex justify-center pt-10">
      <div
        className="p-3 rounded-lg"
        style={{
          backgroundColor:
            formData.barcodeType === "none" ? "transparent" : "white",
        }}
      >
        {formData.barcodeType === "none" ? (
          <div className="w-32 h-32" />
        ) : (
          <div className="flex flex-col items-center">
            <BarcodeImage barcodeType={formData.barcodeType} />
            <span className="text-xs text-gray-500 mt-2 font-mono">QR TEXT</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Google Wallet loyalty-card front. Layout (top → bottom): logo + program name
// bar with a divider, large program title, a row of fields (left + right), the
// barcode with its id, then the hero image at the very bottom. The whole card
// uses the configured background/text colors.
function GoogleFront({ formData, getStampIconSource }) {
  const textColor = formData.textColor;
  const leftField = formData.preview?.leftField;
  const rightField = formData.preview?.rightField;
  // Google maps left_field -> loyaltyPoints and right_field -> secondaryLoyaltyPoints.
  // There is NO equivalent of Apple's header_field, so it is never shown here.
  const rightCol = rightField?.label || rightField?.value ? rightField : null;

  return (
    <div className="rounded-lg flex flex-col items-center p-6">
      <div
        className="min-w-[380px] max-w-[380px] rounded-xl overflow-hidden shadow-lg"
        style={{ backgroundColor: formData.backgroundColor }}
      >
        {/* Logo + program name bar */}
        <div
          className="px-4 py-3 flex items-center gap-2 border-b"
          style={{ borderColor: (textColor || "#000") + "26" }}
        >
          <img
            src={formData.companyLogo}
            alt="Logo"
            className="h-8 w-8 rounded-full object-cover"
          />
          <span className="text-sm font-medium" style={{ color: textColor }}>
            {formData.company}
          </span>
        </div>

        <div className="px-4 pt-4">
          {/* Large title */}
          <h3
            className="text-3xl font-normal leading-tight"
            style={{ color: textColor }}
          >
            {formData.company}
          </h3>

          {/* Fields row */}
          <div className="flex justify-between items-start gap-4 mt-4">
            {(leftField?.label || leftField?.value) && (
              <div>
                <p
                  className="text-xs uppercase tracking-wide"
                  style={{ color: textColor, opacity: 0.7 }}
                >
                  {leftField.label}
                </p>
                <p className="text-lg" style={{ color: textColor }}>
                  {leftField.value ?? ""}
                </p>
              </div>
            )}
            {rightCol && (
              <div className="text-right">
                <p
                  className="text-xs uppercase tracking-wide"
                  style={{ color: textColor, opacity: 0.7 }}
                >
                  {rightCol.label}
                </p>
                <p className="text-lg" style={{ color: textColor }}>
                  {rightCol.value}
                </p>
              </div>
            )}
          </div>

          {/* Barcode + id */}
          {formData.barcodeType !== "none" && (
            <div className="flex flex-col items-center py-6">
              <div className="bg-white p-4 rounded-2xl">
                <BarcodeImage barcodeType={formData.barcodeType} />
              </div>
              <span
                className="mt-3 text-base font-semibold font-mono"
                style={{ color: textColor }}
              >
                QR TEXT
              </span>
            </div>
          )}
        </div>

        {/* Hero image (bottom) */}
        <StampStrip formData={formData} getStampIconSource={getStampIconSource} />
      </div>
    </div>
  );
}

export default function PassPreview({
  formData,
  previewRef,
  getStampIconSource,
}) {
  const { t } = useLanguage();
  const { selectedStore } = useStore();
  const [side, setSide] = useState("front");
  const [platform, setPlatform] = useState("ios");
  const showBack = side === "back";
  // The Apple front is the only capture source; it's the visible front only on iOS.
  const showAppleFront = !showBack && platform === "ios";

  // "Powered By Loyaltify.io" only appears for starter/free plans (matches the
  // wallet services), and sits at the bottom of the list.
  const isStarterPlan =
    !selectedStore?.current_plan || selectedStore.current_plan === "starter";
  const { customFields, links, hasMerchantContent } = getBackContent(
    formData,
    isStarterPlan,
  );

  // Milestone stamp cards always print the reward list on the back (the wallet
  // prepends it in build_back_fields), so the preview must show it too — not the
  // "back is optional / leave it blank" placeholder.
  const milestoneSummary =
    formData.type === "stamp" && formData.stampRewardMode === "milestone"
      ? rewardSummaryPreview(formData.initialPoints, formData.milestones, t)
      : "";
  const rewardRow = milestoneSummary
    ? {
        label: t("passes.form.walletLabels.rewards"),
        value: milestoneSummary,
        link: false,
      }
    : null;

  // Apple renders custom fields and links together as one flat label/value list.
  const iosRows = [
    ...(rewardRow ? [rewardRow] : []),
    ...customFields.map((f) => ({ label: f.label, value: f.value, link: false })),
    ...links.map((l) => ({ label: l.label, value: l.value, link: true })),
  ];
  return (
    // translate="no": the preview mirrors the merchant's actual wallet pass,
    // which is never machine-translated. It also keeps Google Translate's DOM
    // rewrites out of this frequently re-rendered subtree (see domPatches.js).
    <div className="w-full" translate="no">
      <div className="sticky" style={{ top: "5rem" }}>
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4">
            <div className="flex space-x-3 mb-6">
              <button
                type="button"
                onClick={() => setSide("front")}
                className={`w-full rounded-lg py-2.5 text-sm font-medium leading-5 border border-gray-200 ${
                  !showBack
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                }`}
              >
                {t("passes.form.preview.front")}
              </button>
              <button
                type="button"
                onClick={() => setSide("back")}
                className={`w-full rounded-lg py-2.5 text-sm font-medium leading-5 border border-gray-200 ${
                  showBack
                    ? "bg-white text-indigo-600 shadow-sm"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                }`}
              >
                {t("passes.form.preview.back")}
              </button>
            </div>
            {/* Platform sub-toggle — applies to whichever face is shown, since
                Apple and Google render both faces differently. */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setPlatform("ios")}
                  className={`px-4 py-1 rounded-md transition-colors ${
                    platform === "ios"
                      ? "bg-gray-900 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  iOS
                </button>
                <button
                  type="button"
                  onClick={() => setPlatform("android")}
                  className={`px-4 py-1 rounded-md transition-colors ${
                    platform === "android"
                      ? "bg-gray-900 text-white"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Android
                </button>
              </div>
            </div>
            <div className="relative">
            {/* Apple front — always mounted (capture source); shown only when the
                front + iOS are selected, otherwise moved off-screen at full size
                so the preview-image capture keeps working. */}
            <div
              style={
                showAppleFront
                  ? undefined
                  : { position: "absolute", left: "-99999px", top: 0 }
              }
              aria-hidden={!showAppleFront}
            >
            <div className="aspect-[1.5] rounded-lg flex items-center justify-center p-6">
              <div
                className="min-w-[380px] max-w-[380px] rounded-xl overflow-hidden shadow-lg pass-preview-container"
                style={{ backgroundColor: formData.backgroundColor }}
              >
                {/* Header with Logo and Points */}
                <div className="pl-3 pb-1 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <img
                      src={formData.companyLogo}
                      alt="Logo"
                      className="h-12 w-12 object-cover"
                    />
                    <span
                      className="text-l font-bold"
                      style={{ color: formData.textColor }}
                    >
                      {formData.company}
                    </span>
                  </div>
                  <div className="flex justify-end">
                    <div
                      className="rounded-full px-4 py-1"
                      style={{ borderColor: formData.textColor }}
                    >
                      <span
                        style={{ color: formData.textColor }}
                        className="text-xs font-medium"
                      >
                        <span className="uppercase">
                          {formData.preview?.headerField?.label || ""}
                        </span>
                        <br />
                        <span
                          style={{ color: formData.textColor }}
                          className="text-lg font-medium flex justify-end"
                        >
                          {formData.preview?.headerField?.value ?? ""}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Background Image with Stamps Overlay (capture source) */}
                <StampStrip
                  formData={formData}
                  getStampIconSource={getStampIconSource}
                  innerRef={previewRef}
                />

                {/* Customer Info and QR Code */}
                <div className="p-4 pt-2 space-y-4">
                  <div className="grid grid-cols-2 gap-12 font-medium">
                    {formData.preview?.leftField?.label && (
                      <div>
                        <p
                          className="text-sm uppercase"
                          style={{ color: formData.textColor }}
                        >
                          {formData.preview.leftField.label}
                        </p>
                        <p
                          className="text-lg font-medium"
                          style={{ color: formData.textColor }}
                        >
                          {formData.preview.leftField.value ?? ""}
                        </p>
                      </div>
                    )}
                    {formData.preview?.rightField?.label && (
                      <div className="ml-auto">
                        <p
                          className="text-left text-sm uppercase"
                          style={{ color: formData.textColor }}
                        >
                          {formData.preview.rightField.label}
                        </p>
                        <p
                          className="text-left text-lg font-medium"
                          style={{ color: formData.textColor }}
                        >
                          {formData.preview.rightField.value ?? ""}
                        </p>
                      </div>
                    )}
                  </div>
                  <BarcodeBlock formData={formData} />
                </div>
              </div>
            </div>
            </div>

            {/* Google front — rendered in place of the Apple front on Android. */}
            {!showBack && platform === "android" && (
              <GoogleFront
                formData={formData}
                getStampIconSource={getStampIconSource}
              />
            )}

            {/* Back face — Apple and Google render the same data differently; the
                platform toggle above switches between the two accurate layouts. */}
            {showBack && (
              <div className="rounded-lg p-6">
                <div className="flex items-start justify-center">
                  <div className="min-w-[380px] max-w-[380px]">
                    {/* iOS Wallet — flat grouped list, label left / value right,
                        long values wrap full-width below the label. */}
                    {platform === "ios" && iosRows.length > 0 && (
                      <div className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
                        {iosRows.map((row, index) => {
                          const value = row.value || "";
                          const isLong = value.length > 24;
                          return (
                            <div key={index} className="px-4">
                              <div
                                className={`py-3 ${
                                  index !== 0 ? "border-t border-gray-200" : ""
                                }`}
                              >
                                {isLong ? (
                                  <div>
                                    <p className="text-gray-900 text-base leading-snug">
                                      {row.label}
                                    </p>
                                    <p
                                      className="mt-1 whitespace-pre-line text-sm leading-snug break-words"
                                      style={{
                                        color: row.link ? "#007aff" : "#8e8e93",
                                      }}
                                    >
                                      {value}
                                    </p>
                                  </div>
                                ) : (
                                  <div className="flex items-start justify-between gap-4">
                                    <p className="text-gray-900 text-base leading-snug">
                                      {row.label}
                                    </p>
                                    <p
                                      className="text-base leading-snug text-right break-words"
                                      style={{
                                        color: row.link ? "#007aff" : "#8e8e93",
                                      }}
                                    >
                                      {value}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Google Wallet — custom fields as stacked text modules,
                        all links grouped under a single "Links" section. */}
                    {platform === "android" && (
                      <div className="space-y-4">
                        {milestoneSummary && (
                          <div className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
                            <div className="px-4 py-3">
                              <p className="text-xs uppercase tracking-wide text-gray-500">
                                {t("passes.form.walletLabels.rewards")}
                              </p>
                              <p className="mt-1 whitespace-pre-line text-sm leading-snug text-gray-900 break-words">
                                {milestoneSummary}
                              </p>
                            </div>
                          </div>
                        )}
                        {customFields.length > 0 && (
                          <div className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
                            {customFields.map((field, index) => (
                              <div key={index} className="px-4">
                                <div
                                  className={`py-3 ${
                                    index !== 0 ? "border-t border-gray-200" : ""
                                  }`}
                                >
                                  <p className="text-xs uppercase tracking-wide text-gray-500">
                                    {field.label}
                                  </p>
                                  <p className="mt-1 text-sm leading-snug text-gray-900 break-words">
                                    {field.value}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {links.length > 0 && (
                          <div>
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1 px-1">
                              Links
                            </p>
                            <div className="rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
                              {links.map((link, index) => (
                                <div key={index} className="px-4">
                                  <div
                                    className={`py-3 ${
                                      index !== 0
                                        ? "border-t border-gray-200"
                                        : ""
                                    }`}
                                  >
                                    <p
                                      className="text-base leading-snug break-words"
                                      style={{ color: "#1a73e8" }}
                                    >
                                      {link.android}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {!hasMerchantContent && !milestoneSummary && (
                      <p className="text-sm text-center text-gray-400 mt-4">
                        {t("passes.form.preview.backEmpty")}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
